import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, X, Search } from 'lucide-react';
import { readResponse, readResponseJSON } from '../utils/fetchSafe';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Modal for merging an inactive skeleton employee into an active real employee.
 * Rewrites every project name-field, every pm_rows entry, and every timeslip
 * that references the skeleton's canonical name to point at the target's
 * canonical name. Then deletes the skeleton.
 */
const MergeEmployeeModal = ({ skeleton, activeUsers, onClose, onMerged }) => {
  const [query, setQuery] = useState('');
  const [targetEmail, setTargetEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = activeUsers.filter((u) =>
      `${u.last_name || ''} ${u.first_name || ''} ${u.email || ''}`
        .toLowerCase()
        .includes(q)
    );
    return list.slice(0, 20);
  }, [activeUsers, query]);

  const targetUser = activeUsers.find((u) => u.email === targetEmail);
  const sourceCanonical = `${skeleton.last_name || ''}, ${skeleton.first_name || ''}`.replace(/^, |, $/g, '');
  const targetCanonical = targetUser
    ? `${targetUser.last_name || ''}, ${targetUser.first_name || ''}`.replace(/^, |, $/g, '')
    : '';

  const handleMerge = async () => {
    if (!targetEmail) {
      toast.error('Pick a target employee first.');
      return;
    }
    if (!window.confirm(
      `Merge "${sourceCanonical}" into "${targetCanonical}"?\n\n` +
      `Every project name-field, pm_row, and timeslip that references "${sourceCanonical}" will be rewritten ` +
      `to "${targetCanonical}". The skeleton record will be deleted. This cannot be undone.`
    )) return;

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/merge-employee`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          source_first_name: skeleton.first_name || '',
          source_last_name: skeleton.last_name || '',
          target_email: targetEmail,
        }),
      });
      const { data } = await readResponse(res);
      if (!res.ok) throw new Error(data?.detail || 'Merge failed');
      toast.success(
        `Merged. ${data.projects_name_field_updates + data.projects_pm_rows_updated} project updates, ` +
        `${data.timeslips_updated} timeslip${data.timeslips_updated === 1 ? '' : 's'}.`
      );
      onMerged();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="merge-modal-backdrop"
    >
      <div
        className="bg-white rounded shadow-xl w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
        data-testid="merge-modal"
      >
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="text-lg text-gray-700 font-light italic">Merge skeleton employee</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="text-sm">
            <div className="text-gray-500">Merging</div>
            <div className="font-mono text-gray-800">{sourceCanonical || '(no name)'}</div>
            <div className="text-xs text-gray-400 italic mt-0.5">
              Inactive skeleton{skeleton.import_mentioned_in?.length ? ` — mentioned in: ${skeleton.import_mentioned_in.join(', ')}` : ''}
            </div>
          </div>

          <div>
            <div className="text-sm text-gray-500 mb-1">Into (target — must be an Active user)</div>
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search active users by name or email…"
                className="w-full border border-gray-300 rounded pl-8 pr-3 py-2 text-sm"
                data-testid="merge-target-search"
              />
            </div>
            <div className="mt-2 border border-gray-200 rounded max-h-56 overflow-y-auto" data-testid="merge-candidates">
              {candidates.length === 0 ? (
                <div className="p-3 text-xs text-gray-400 italic">No matches</div>
              ) : (
                candidates.map((u) => (
                  <label
                    key={u.email || `${u.last_name}-${u.first_name}`}
                    className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer border-b border-gray-100 last:border-b-0 ${
                      targetEmail === u.email ? 'bg-orange-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="merge-target"
                      checked={targetEmail === u.email}
                      onChange={() => setTargetEmail(u.email)}
                      disabled={!u.email}
                      className="accent-[#FFA94D]"
                    />
                    <div className="flex-1">
                      <div className="text-gray-800 font-mono">{u.last_name}, {u.first_name}</div>
                      <div className="text-xs text-gray-500">{u.email || '(no email)'}</div>
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>

          {targetCanonical && (
            <div className="bg-orange-50 border border-orange-200 rounded p-3 text-xs text-orange-900">
              <strong>Preview:</strong> every project name-field, pm_row, and timeslip with{' '}
              <span className="font-mono">{sourceCanonical}</span> →{' '}
              <span className="font-mono">{targetCanonical}</span>. The skeleton record will then be deleted.
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t px-5 py-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-100"
            data-testid="merge-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={handleMerge}
            disabled={!targetEmail || submitting}
            className="px-4 py-2 text-sm text-white bg-[#FFA94D] hover:bg-[#F08A2A] disabled:opacity-50 rounded flex items-center gap-2"
            data-testid="merge-confirm-btn"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Merge
          </button>
        </div>
      </div>
    </div>
  );
};

export default MergeEmployeeModal;
